import { fromArrayBuffer, GeoTIFFImage } from 'geotiff';

export interface TiffImageUrls {
    baseUrl: string;
    mergedUrl: string;
}

export class TiffToImageConverter {

    public static async getTiffImageUrls(tiffUrl: string): Promise<TiffImageUrls> {
        if (!tiffUrl) throw new Error('TIFF URL must be provided.');

        try {
            const response = await fetch(tiffUrl);
            if (!response.ok) {
                return { baseUrl: '../../../assets/image_not_found.jpg', mergedUrl: '../../../assets/image_not_found.jpg' as string };
            } 
                    
            const arrayBuffer = await response.arrayBuffer();

            const tiff = await fromArrayBuffer(arrayBuffer);

            // The first image in the TIFF is the base image.
            const baseImage = await tiff.getImage(0);
            const baseCanvas = await this.geoTiffImageToCanvas(baseImage);
            const baseUrl = baseCanvas.toDataURL('image/jpeg');

            // The second image is the mask. We need to advance to it.
            let mergedUrl = baseUrl; // Default to baseUrl if no second image
            const imageCount = await tiff.getImageCount();
            if (imageCount > 1) {
                const contourImage = await tiff.getImage(1);
                const contourCanvas = await this.geoTiffImageToCanvas(contourImage);
                this.makeTransparent(contourCanvas);
                const mergedCanvas = this.mergeImages(baseCanvas, contourCanvas);
                mergedUrl = mergedCanvas.toDataURL('image/jpeg');
            }

            return { baseUrl, mergedUrl: mergedUrl as string };
        } catch (error) {
            console.error('Error processing TIFF file:', error);
            throw error;
        }
    }

    private static async geoTiffImageToCanvas(image: GeoTIFFImage): Promise<HTMLCanvasElement> {
        const width = image.getWidth();
        const height = image.getHeight();

        // Determine if the image is palette-based
        const isPalette = !!image.fileDirectory.ColorMap;
        let rgba: Uint8Array;

        if (isPalette) {
            // 1. Handle Palette-based images (e.g., LZW, single-band with ColorMap)
            const rasters = await image.readRasters({ interleave: true });
            rgba = this.paletteToRGBA(rasters as Uint8Array, image.fileDirectory.ColorMap!, width, height);

        } else {
            // 2. Handle RGB / Grayscale
            try {
                // Attempt to read as RGB. GeoTIFF.js 'readRGB' often handles 3-band (RGB) and 1-band (Grayscale)
                // images by converting them to an interleaved 4-band (RGBA) array.
                rgba = await image.readRGB({ interleave: true }) as Uint8Array;

                // Check if readRGB returned 3 bands (RGB) instead of 4 (RGBA), which can happen
                // in some configurations and would require manual conversion to RGBA.
                if (rgba.length === width * height * 3) {
                    const rgb = rgba; // Rename for clarity
                    rgba = new Uint8Array(width * height * 4);
                    for (let i = 0, j = 0; i < rgb.length; i += 3, j += 4) {
                        rgba[j] = rgb[i];         // R
                        rgba[j + 1] = rgb[i + 1]; // G
                        rgba[j + 2] = rgb[i + 2]; // B
                        rgba[j + 3] = 255;        // A (Full opacity)
                    }
                } else if (rgba.length !== width * height * 4) {
                    // fall back to reading rasters for grayscale conversion.
                    throw new Error('readRGB did not return a standard 3-band or 4-band result.');
                }

            } catch (error) {
                // 3. Fallback: Read Rasters and convert to Grayscale RGBA (1-band images)
                console.warn("Falling back to single-band grayscale conversion:", error);
                const rasters = await image.readRasters({ interleave: true }) as Uint8Array;

                // Check if it's a single band (rasters.length === width * height)
                if (rasters.length === width * height) {
                    rgba = new Uint8Array(width * height * 4);
                    for (let i = 0; i < rasters.length; i++) {
                        const value = rasters[i];
                        rgba[i * 4] = value;
                        rgba[i * 4 + 1] = value;
                        rgba[i * 4 + 2] = value;
                        rgba[i * 4 + 3] = 255;
                    }
                } else {
                    // If the raster count doesn't match a single-band grayscale, throw an error.
                    throw new Error('Unsupported GeoTIFF format for canvas conversion.');
                }
            }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get 2D context from canvas');

        const imageData = ctx.createImageData(width, height);
        imageData.data.set(rgba);
        ctx.putImageData(imageData, 0, 0);
        return canvas;
    }

    private static paletteToRGBA(indices: Uint8Array, colorMap: number[], width: number, height: number): Uint8Array {
        const rgba = new Uint8Array(width * height * 4);
        const nColors = colorMap.length / 3; // TIFF stores separate R,G,B arrays
        const rMap = colorMap.slice(0, nColors);
        const gMap = colorMap.slice(nColors, nColors * 2);
        const bMap = colorMap.slice(nColors * 2, nColors * 3);

        for (let i = 0; i < indices.length; i++) {
            const idx = indices[i];
            rgba[i * 4] = rMap[idx] ?? 0;
            rgba[i * 4 + 1] = gMap[idx] ?? 0;
            rgba[i * 4 + 2] = bMap[idx] ?? 0;
            rgba[i * 4 + 3] = 255;
        }
        return rgba;
    }

    private static makeTransparent(canvas: HTMLCanvasElement) {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imgData.data;

        for (let i = 0; i < data.length; i += 4) {
            if (data[i] === 0 && data[i + 1] === 0 && data[i + 2] === 0) {
                data[i + 3] = 0; // make black transparent
            }
        }

        ctx.putImageData(imgData, 0, 0);
    }

    private static mergeImages(baseCanvas: HTMLCanvasElement, contourCanvas: HTMLCanvasElement): HTMLCanvasElement {
        const merged = document.createElement('canvas');
        merged.width = baseCanvas.width;
        merged.height = baseCanvas.height;

        const ctx = merged.getContext('2d');
        if (!ctx) return merged;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, merged.width, merged.height);
        ctx.drawImage(baseCanvas, 0, 0);
        ctx.drawImage(contourCanvas, 0, 0);

        return merged;
    }
}
